import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightIcon,
  Users,
  User,
  Building2,
  Mail,
  Phone,
  Calendar,
  FileText,
  MessageSquare,
} from "lucide-react";
import { DownloadIcon } from "../../components/icons";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import VerifierApprovalModal from "../../common/VerifierApprovalModal/VerifierApprovalModal";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import { formatDate } from "../../utils/formatDate";
import { detectFileType } from "../../utils/DetectFileType";
import renderPdf from "../../common/RenderPdf/RenderPdf";
import DocumentPreviewModal from "../../common/DocumentPreviewModal/DocumentPreviewModal";
import { useTranslation } from "react-i18next";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const VerifierApprovalDetails = () => {
  const { org_code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State
  const [verifierDetails, setVerifierDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState("");
  const [issuerComments, setIssuerComments] = useState("");
  const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PDF states for carousel
  const [pdfLoaded, setPdfLoaded] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const carouselRef = useRef(null);
  const carouselCanvasRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const documentLabels = {
    incorporation_certificate: t(
      "verifierApproval:organisation_inc_certificate",
    ),
    gst_certificate: t("verifierApproval:gst_certificate"),
  };

  // Fetch verifier details from API
  useEffect(() => {
    const fetchVerifierDetails = async () => {
      try {
        setLoading(true);

        const response = await GET(
          `${EntityConstants.TENANT_VERIFIER_APPROVALS}/${org_code}`,
        );
        const data = await response.json();

        const detail = data?.result || {};
        setVerifierDetails(detail);

        // Process documents from API response
        const processedDocs = [];
        Object.keys(documentLabels).forEach((key) => {
          if (detail[key]) {
            const fileType = detectFileType(detail[key]);
            processedDocs.push({
              id: key,
              title: documentLabels[key],
              type: fileType,
              url: detail[key],
              key: key,
            });
          }
        });

        setDocuments(processedDocs);
      } catch (error) {
        console.error("Error fetching verifier details:", error);
        Toast({
          type: "error",
          title: t("verifierApproval:error"),
          message: t("verifierApproval:failed_load_verifier_details"),
        });
      } finally {
        setLoading(false);
      }
    };

    if (org_code) {
      fetchVerifierDetails();
    }
  }, [org_code]);

  useEffect(() => {
    setImageLoaded(false);
    const currentDoc = documents[currentDocumentIndex];
    if (currentDoc?.type === "pdf" && carouselCanvasRef.current) {
      setPdfLoaded(false);
      setPdfLoading(true);

      const timer = setTimeout(() => {
        renderPdf(currentDoc.url, carouselCanvasRef, 0.5)
          .then(() => {
            setPdfLoaded(true);
            setPdfLoading(false);
          })
          .catch((error) => {
            console.error("Failed to render PDF in carousel:", error);
            setPdfLoading(false);
          });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentDocumentIndex, documents]);

  // Combine data from navigation state and API response

  const handleAccept = () => {
    setActionType("Approved");
    setIssuerComments("");
    setIsModalOpen(true);
  };

  const handleReject = () => {
    setActionType("Rejected");
    setIssuerComments("");
    setIsModalOpen(true);
  };

  const handleBackClick = () => {
    navigate("/issuer/verifier-approvals", {
      state: {
        filters: location.state?.filters,
      },
    });
  };

  const handleConfirmAction = async (actionType, comment = "") => {
    if (actionType === "Rejected" && (!comment || comment.trim() === "")) {
      Toast({
        type: "error",
        title: t("verifierApproval:validation_error"),
        message: t("verifierApproval:comments_required_rejecting_verifier"),
      });
      return;
    }

    const body = {
      verifier_id: verifierDetails.verifier_id,
      approval_status: actionType,
      ...(comment.trim() && { approver_comments: comment.trim() }),
    };

    try {
      setIsSubmitting(true);

      const response = await POST(
        EntityConstants.TENANT_VERIFIER_APPROVALS,
        body,
      );

      const data = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: t("verifierApproval:success"),
          message:
            data.message ||
            `${t(
              "verifierApproval:verifier_has_been",
            )} ${actionType.toLowerCase()} ${t(
              "verifierApproval:successfully",
            )}!`,
        });

        setVerifierDetails((prev) => ({
          ...prev,
          approval_status: actionType,
          issuer_comments: comment.trim() || null,
        }));

        setTimeout(() => {
          navigate("/issuer/verifier-approvals", {
            state: {
              filters: location.state?.filters,
              refresh: true,
            },
          });
        }, 2000);
      } else {
        Toast({
          type: "error",
          title: t("verifierApproval:error"),
          message:
            data.message ||
            t("verifierApproval:error_updating_verification_status"),
        });
      }
    } catch (error) {
      console.error("Error updating verification status:", error);
      Toast({
        type: "error",
        title: t("verifierApproval:error"),
        message: t(
          "verifierApproval:error_occurred_updating_verification_status",
        ),
      });
    } finally {
      setIsSubmitting(false);
      setIsModalOpen(false);
    }
  };

  // Carousel navigation
  const nextDocument = () => {
    if (documents.length > 0) {
      setCurrentDocumentIndex((prevIndex) =>
        prevIndex === documents.length - 1 ? 0 : prevIndex + 1,
      );
    }
  };

  const prevDocument = () => {
    if (documents.length > 0) {
      setCurrentDocumentIndex((prevIndex) =>
        prevIndex === 0 ? documents.length - 1 : prevIndex - 1,
      );
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      if (!doc || !doc.url) {
        Toast({
          type: "error",
          title: t("verifierApproval:error"),
          message: t("verifierApproval:no_document_url_found"),
        });
        return;
      }

      const response = await fetch(doc.url);
      if (!response.ok) {
        throw new Error(
          `${t("verifierApproval:failed_fetch_document")}: ${response.status}`,
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const filename = doc.title
        ? `${doc.title}.${
            doc.type === "pdf" ? "pdf" : getExtensionFromUrl(doc.url)
          }`
        : `document.${
            doc.type === "pdf" ? "pdf" : getExtensionFromUrl(doc.url)
          }`;

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      Toast({
        type: "success",
        title: t("verifierApproval:download_started"),
        message: `${doc.title} ${t("verifierApproval:download_has_started")}.`,
      });
    } catch (error) {
      console.error("Download error:", error);

      // Fallback: Direct download
      const link = document.createElement("a");
      link.href = doc.url;
      link.download = doc.title || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      Toast({
        type: "info",
        title: t("verifierApproval:download"),
        message: t("verifierApproval:document_download_initiated"),
      });
    }
  };

  // Helper function to extract extension from URL
  const getExtensionFromUrl = (url) => {
    if (!url) return "";
    const urlWithoutQuery = url.split("?")[0];
    const parts = urlWithoutQuery.split(".");
    if (parts.length > 1) {
      return parts.pop().toLowerCase();
    }
    return "";
  };

  const handleViewDocument = (document) => {
    if (!document) return;
    setShowPdfModal(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (loading || !verifierDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-start justify-center" id={pageRootTestIds.verifierApprovalDetails}>
        <div className="mt-40">
          <CfLoader />
        </div>
      </div>
    );
  }

  const currentStatus =
    verifierDetails?.approval_status === "Approved" ||
    verifierDetails?.approval_status === "Rejected"
      ? verifierDetails.approval_status
      : "Approval Pending";

  const currentDocument = documents[currentDocumentIndex];

  return (
    <div
      className="min-h-screen bg-cfSecondary pt-20 pb-10"
      id={pageRootTestIds.verifierApprovalDetails}
    >
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
            <h2 className="text-lg font-medium flex items-center justify-center w-full">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <button
                  onClick={handleBackClick}
                  id="verifier-approval-details-breadcrumb-back"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleBackClick();
                    }
                  }}
                  className="hover:text-gray-800 cursor-pointer focus:outline-none"
                >
                  {t("verifierApproval:verifier_approvals")}
                </button>
                <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                <span className="text-gray-800 font-medium cursor-default">
                  {verifierDetails.org_name ||
                    t("verifierApproval:organisation")}
                </span>
              </div>
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center">
            {currentStatus === "Approval Pending" ? (
              <div className="flex items-start gap-4">
                <button
                  onClick={handleAccept}
                  id="verifier-approval-details-approve"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAccept();
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex rounded-[8px] items-center justify-center gap-2 w-[10rem] h-[2.5rem] bg-cfPrimaryButton text-white font-medium lg:font-semibold text-sm lg:text-md hover:bg-cfPrimaryDark transition duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? t("verifierApproval:processing")
                    : t("verifierApproval:approve")}
                </button>

                <button
                  onClick={handleReject}
                  id="verifier-approval-details-reject"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleReject();
                    }
                  }}
                  disabled={isSubmitting}
                  className="flex rounded-[8px] items-center justify-center gap-2 w-[10rem] h-[2.5rem] bg-white text-black font-medium lg:font-semibold text-sm lg:text-md border border-gray-800 hover:!bg-gray-100 transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? t("verifierApproval:processing")
                    : t("verifierApproval:reject")}
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <div
                  className={`px-4 py-2 rounded-[8px] ${
                    currentStatus === "Approved"
                      ? "bg-cfPrimaryButton text-white"
                      : "bg-red-500 text-white"
                  }`}
                >
                  <span className="text-white font-medium">
                    {t("verifierApproval:status")}:{" "}
                    {currentStatus === "Approved"
                      ? t("verifierApproval:approved")
                      : t("verifierApproval:rejected")}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto p-4">
        <div
          className={`grid grid-cols-1 ${
            documents.length > 0 ? "lg:grid-cols-3" : "lg:grid-cols-1"
          } gap-6`}
        >
          <div
            className={`space-y-6 ${
              documents.length > 0 ? "lg:col-span-2" : "lg:col-span-1"
            }`}
          >
            <div className="bg-white rounded-[16px] shadow-md border-t border-[#eeeeee] p-6">
              <div className="flex items-center text-center mb-6 gap-2">
                <Users className="w-5 h-5 text-gray-800" />
                <h2 className="text-lg font-semibold text-cfHeadingText">
                  {t("verifierApproval:verifier_details")}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center text-center">
                      <User className="w-5 h-5 text-gray-500" />
                      <span className="text-md text-gray-500 block font-medium">
                        {t("verifierApproval:verifier_name")}
                      </span>
                    </div>

                    <p className="text-gray-900 text-md font-medium">
                      {verifierDetails.verifier_name ||
                        t("verifierApproval:na")}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center text-center">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <span className="text-md text-gray-500 block font-medium">
                        {t("verifierApproval:email")}
                      </span>
                    </div>

                    <p className="text-gray-900 text-md font-medium">
                      {verifierDetails.email || t("verifierApproval:na")}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center text-center">
                      <Calendar className="w-5 h-5 text-gray-500" />
                      <span className="text-md text-gray-500 block font-medium">
                        {t("verifierApproval:requested_at")}
                      </span>
                    </div>

                    <p className="text-gray-900 text-md font-medium">
                      {verifierDetails.created_at
                        ? formatDate(verifierDetails.created_at)
                        : t("verifierApproval:na")}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center text-center">
                      <Building2 className="w-5 h-5 text-gray-500" />
                      <span className="text-md text-gray-500 block font-medium">
                        {t("verifierApproval:organisation")}
                      </span>
                    </div>

                    <p className="text-gray-900 text-md font-medium">
                      {verifierDetails.org_name || t("verifierApproval:na")}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-2 items-center text-center">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <span className="text-md text-gray-500 block font-medium">
                        {t("verifierApproval:phone_number")}
                      </span>
                    </div>

                    <p className="text-gray-900 text-md font-medium">
                      {verifierDetails.phone_no || t("verifierApproval:na")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Issuer Comments (if approved/rejected) */}
              {verifierDetails.issuer_comments && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {currentStatus === "Approved"
                      ? t("verifierApproval:approval_comment")
                      : t("verifierApproval:rejection_reason")}
                  </h3>
                  <div
                    className={`rounded-[12px] p-4 ${
                      currentStatus === "Approved" ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    <p
                      className={`${
                        currentStatus === "Approved"
                          ? "text-green-700"
                          : "text-red-700"
                      }`}
                    >
                      {verifierDetails.issuer_comments}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {/* Comments Block */}
            {verifierDetails.comments && (
              <div className="bg-white rounded-[16px] shadow-md border-t border-[#eeeeee] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5 text-gray-800" />
                  <h2 className="text-lg font-semibold text-cfHeadingText">
                    {t("verifierApproval:comments")}
                  </h2>
                </div>

                <div className="bg-gray-50 rounded-[12px] p-5">
                  <p className="text-gray-800 leading-relaxed">
                    {verifierDetails.comments}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Documents */}
          {documents.length > 0 && (
            <div className="lg:col-span-1">
              <div className="bg-white rounded-[16px] shadow-md border-t border-[#eeeeee] p-6 sticky top-24">
                <div className="flex gap-2 items-center text-center mb-6">
                  <FileText className="w-5 h-5 text-gray-500" />
                  <h2 className="text-lg font-semibold text-cfHeadingText">
                    {t("verifierApproval:documents")}
                  </h2>
                </div>

                {documents.length > 0 ? (
                  <div className="space-y-4">
                    <div className="relative" ref={carouselRef}>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                        {currentDocument?.title ||
                          t("verifierApproval:documents")}
                      </h3>
                      <div
                        className="w-full aspect-video rounded-[16px] border-2 border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center cursor-pointer mb-4"
                        onClick={() => handleViewDocument(currentDocument)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleViewDocument(currentDocument);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`View ${currentDocument?.title} document`}
                      >
                        {currentDocument?.type === "image" ? (
                          <div className="h-full w-full flex items-center justify-center relative">
                            <img
                              src={currentDocument?.url}
                              alt={currentDocument?.title}
                              className={`w-full h-full object-contain ${
                                imageLoaded ? "opacity-100" : "opacity-0"
                              } transition-opacity duration-200`}
                              onLoad={handleImageLoad}
                              onError={() => setImageLoaded(true)}
                            />
                            {!imageLoaded && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-gray-500 text-center">
                                  <svg
                                    className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    ></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    ></path>
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : currentDocument?.type === "pdf" ? (
                          <div className="h-full w-full flex items-center justify-center relative">
                            <canvas
                              ref={carouselCanvasRef}
                              className={`w-full h-full ${
                                pdfLoaded ? "opacity-100" : "opacity-0"
                              } transition-opacity duration-200`}
                            />
                            {!pdfLoaded && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                {pdfLoading ? (
                                  <div className="text-gray-500 text-center">
                                    <svg
                                      className="animate-spin h-6 w-6 mx-auto mb-2 text-blue-600"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      ></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                      ></path>
                                    </svg>
                                  </div>
                                ) : (
                                  <div className="text-gray-500 text-center">
                                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-3">
                                      <span className="text-red-600 font-bold text-xl">
                                        {t("verifierApproval:pdf")}
                                      </span>
                                    </div>
                                    <p className="text-gray-600">
                                      {t(
                                        "verifierApproval:loading_pdf_preview",
                                      )}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center p-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                              <span className="text-gray-600 font-bold text-xl">
                                ?
                              </span>
                            </div>
                            <p className="text-gray-600 text-center">
                              {t("verifierApproval:unsupported_file_type")}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Navigation and Counter Container */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            prevDocument();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              prevDocument();
                            }
                          }}
                          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Previous document"
                          disabled={documents.length <= 1}
                        >
                          <ChevronLeft className="h-5 w-5 text-gray-700" />
                        </button>

                        <div className="text-sm text-gray-500 font-medium">
                          {currentDocumentIndex + 1} / {documents.length}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            nextDocument();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              nextDocument();
                            }
                          }}
                          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Next document"
                          disabled={documents.length <= 1}
                        >
                          <ChevronRight className="h-5 w-5 text-gray-700" />
                        </button>
                      </div>

                      {/* Download Current Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (currentDocument) {
                            handleDownloadDocument(currentDocument);
                          }
                        }}
                        className="w-full mt-4 flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-[8px] transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!currentDocument}
                      >
                        <DownloadIcon size={16} className="inline-flex" aria-hidden />
                        {t("verifierApproval:download_current_document")}
                      </button>
                    </div>

                    {/* Download All Button */}
                    <button
                      onClick={() => {
                        documents.forEach((doc) => handleDownloadDocument(doc));
                      }}
                      className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-cfPrimaryButton hover:bg-cfPrimaryDark text-white rounded-[8px] transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={documents.length === 0}
                    >
                      <DownloadIcon size={20} className="inline-flex" aria-hidden />
                      {t("verifierApproval:download_all_documents")}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      {t("verifierApproval:no_documents_available")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PDF Preview Modal  */}
      <DocumentPreviewModal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        document={currentDocument}
        onDownload={handleDownloadDocument}
      />

      {/* Approval Modal */}
      <VerifierApprovalModal
        isModalOpen={isModalOpen}
        toggleModal={setIsModalOpen}
        actionType={actionType}
        handleConfirmAction={() =>
          handleConfirmAction(actionType, issuerComments)
        }
        issuerComments={issuerComments}
        setIssuerComments={setIssuerComments}
      />
    </div>
  );
};

export default VerifierApprovalDetails;
